/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import DefaultApiParameters from '../../enum/DefaultApiParameters';
import {
  ElectricityPrice,
  ElectricityPriceInitialProperties,
  ElectricityPriceRequest,
  ElectricityPriceResponse,
} from '../../interface/electricity-price.interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../shared-functions/ConvertUTCtoDate';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import electricityPrice from '../../utils/api/electricity-price';

import { setNotificationMessage } from './NotificationManagementSlice';

// a thunk to fetch users
export const fetchAllApprovedElectricityPrice = createAsyncThunk<
  ElectricityPriceResponse,
  ElectricityPriceRequest
>(
  'approved-electricity-price/fetchAllApprovedElectricityPrice',
  async (data: ElectricityPriceRequest, { dispatch }) => {
    try {
      const response = await electricityPrice.fetchUnapprovedElectricityPrice(
        removeEmptyKeys({ ...data, status: 'approved' }),
      );
      return {
        ...response.data,
        results: response.data.results.map((stationData: ElectricityPrice) => ({
          ...stationData,
          id: stationData._id,
          createdAt: ConvertUTCtoDate(stationData?.createdAt),
        })),
      };
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message,
          code: error?.response?.data?.code,
          status: true,
        }),
      );
      throw error;
    }
  },
);

// a thunk to fetch devices filters
export const fetchApprovedElectricPriceFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('approved-electricity-price/fetchApprovedElectricPriceFilters', async (_, { dispatch }) => {
  try {
    const response = await electricityPrice.fetchUnapprovedElectricityPriceFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: ElectricityPriceInitialProperties = {
  electricityPricing: [],
  limit: DefaultApiParameters.limit,
  page: DefaultApiParameters.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
};

// Create the slice
export const approvedElectricPriceSlice = createSlice({
  name: 'approved-electricity-price',
  initialState,
  reducers: {
    /**
     * Update device management filters
     * @param state
     * @param action
     */
    updateApprovedElectricPriceFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Function to change isFiltersAppliedStatus
     * @param state
     */
    disableApprovedElectricPriceApplied: (state) => {
      state.isFilterUpdated = false;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder) => {
    builder
      .addCase(fetchAllApprovedElectricityPrice.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllApprovedElectricityPrice.fulfilled, (state, action) => {
        state.loading = false;
        state.electricityPricing = [...action.payload.results];
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      })
      .addCase(fetchAllApprovedElectricityPrice.rejected, (state) => {
        state.loading = false;
        // Handle error if needed
      });
    builder
      .addCase(fetchApprovedElectricPriceFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(fetchApprovedElectricPriceFilters.fulfilled, (state, action) => {
        state.filterListLoading = false;
        state.filters = [...(state.filters || []), ...action.payload];
      })
      .addCase(fetchApprovedElectricPriceFilters.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export the actions
export const { updateApprovedElectricPriceFilters, disableApprovedElectricPriceApplied } =
  approvedElectricPriceSlice.actions;
// Export the reducer
export default approvedElectricPriceSlice.reducer;
